import { useRef, useState } from "react";
import { Modal, Button } from "@etframework/ui.components";
import { useTranslation } from "react-i18next";
import useModalEvent from "../../hooks/useModalEvent";
import ReactDOM from "react-dom";
import { toast } from "react-toastify";
import { useParams } from "react-router-dom";
import { useDeleteCustomFieldGroup } from "../../data/mutations/customFieldGroupMutation";
import { useQueryClient } from "@tanstack/react-query";

export const DeleteGroupConfirmationModal = () => {
  const { t } = useTranslation(["CustomFieldGroup"], {
    useSuspense: false,
  });

  const { taskTypeId } = useParams();
  const queryClient = useQueryClient();

  const [groupId, setGroupId] = useState(null);
  const [groupName, setGroupName] = useState("");

  const { mutate: deleteCustomFieldGroup, isPending } =
    useDeleteCustomFieldGroup();

  const onDeleteGroup = () => {
    deleteCustomFieldGroup(
      { id: groupId },
      {
        onSuccess: () => {
          closeModal();
          toast.success(t("DeleteSuccess"), {
            position: "bottom-left",
          });

          queryClient.invalidateQueries([
            "get-custom-field-groups",
            taskTypeId,
          ]);

          window.dispatchEvent(
            new CustomEvent("DeleteGroupModal:Success", {
              detail: { groupId },
            })
          );
        },
        onError: () => {
          toast.error(t("ErrorMsg"), {
            position: "bottom-left",
          });
        },
      }
    );
  };

  //Modal
  const modalRef = useRef();
  const { ModalEvents, useRegisterEvent } = useModalEvent();
  const [showModal, setShowModal] = useState(false);

  const onModelBeforeClose = () => {};

  const onModalClose = () => {
    setShowModal(false);
  };

  const closeModal = () => {
    setShowModal(false);
    modalRef?.current?.close();
  };

  const openModal = () => {
    setShowModal(true);
    modalRef?.current?.open();
  };

  const toggleModal = (event) => {
    setGroupId(event?.detail?.groupId);
    setGroupName(event?.detail?.groupName);
    if (showModal) {
      closeModal();
      return;
    } else {
      openModal();
    }
  };

  const ModalContent = () => {
    return (
      <div className="et-delete-group-modal">
        <div className="fw-700 fs-16 d-block pb-3">
          {t("DeleteGroupModal.DeleteGroupHeader")}
        </div>

        <div className="pb-3">
          {t("DeleteGroupModal.DeleteGroupMessage", { groupName: groupName })}
        </div>

        <div className="d-flex justify-content-end gap-3 pt-3">
          <Button
            text={t("Cancel")}
            className="btn-tertiary w-auto"
            onClick={toggleModal}
          />
          <Button
            id={ModalEvents.DeleteGroupConfirmationModal.Id + "_delete-button"}
            text={t("Delete")}
            className="btn btn-danger w-auto"
            onClick={onDeleteGroup}
            loading={isPending}
          />
        </div>
      </div>
    );
  };

  useRegisterEvent(ModalEvents.DeleteGroupConfirmationModal, toggleModal);

  return ReactDOM.createPortal(
    <>
      <Modal
        id={ModalEvents.DeleteGroupConfirmationModal.Id}
        ref={modalRef}
        isModal
        showCloseIcon={true}
        visible={showModal}
        content={<ModalContent />}
        close={onModalClose}
        beforeClose={onModelBeforeClose}
        minHeight="276px"
        width="487px"
      ></Modal>
    </>,
    document.body
  );
};
